import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Calculator,
  Percent,
  DollarSign,
  TrendingUp,
  Receipt,
  AlertCircle
} from "lucide-react";
import { BudgetFormData } from "@/types/budget";

interface BudgetSummarySectionProps {
  taxRate?: number;
  currency?: string;
  className?: string;
}

export function BudgetSummarySection({
  taxRate = 0,
  currency = "BRL",
  className = ""
}: BudgetSummarySectionProps) {
  const { control, setValue, getValues, watch } = useFormContext<BudgetFormData>();

  const items = watch("items") || [];
  const discountPercentage = watch("discount_percentage") || 0;
  const discountAmount = watch("discount_amount") || 0;
  const usePercentageDiscount = !discountAmount && discountPercentage > 0;

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.total_value || 0), 0);

  // Calculate discount
  let calculatedDiscount = discountAmount;
  if (usePercentageDiscount) {
    calculatedDiscount = subtotal * (discountPercentage / 100);
  }

  const discountedSubtotal = Math.max(0, subtotal - calculatedDiscount);

  // Calculate tax
  const taxAmount = discountedSubtotal * (taxRate / 100);
  const finalValue = discountedSubtotal + taxAmount;

  // Handle discount type change
  const handleDiscountTypeChange = (usePercentage: boolean) => {
    if (usePercentage) {
      setValue("discount_amount", undefined);
      // Calculate equivalent percentage if there was a fixed amount
      if (discountAmount > 0 && subtotal > 0) {
        const equivalentPercentage = (discountAmount / subtotal) * 100;
        setValue("discount_percentage", Math.round(equivalentPercentage * 100) / 100);
      }
    } else {
      setValue("discount_percentage", undefined);
      // Calculate equivalent amount if there was a percentage
      if (discountPercentage > 0) {
        const equivalentAmount = subtotal * (discountPercentage / 100);
        setValue("discount_amount", Math.round(equivalentAmount * 100) / 100);
      }
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  const hasItems = items.length > 0;
  const hasDiscount = discountPercentage > 0 || discountAmount > 0;
  const hasTax = taxRate > 0;

  return (
    <div className={`space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Resumo Financeiro
          </CardTitle>
          <CardDescription>
            Cálculo automático dos valores do orçamento
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!hasItems ? (
            <div className="text-center py-8 border-2 border-dashed border-muted rounded-lg">
              <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Sem itens no orçamento</h3>
              <p className="text-muted-foreground">
                Adicione itens para ver o resumo dos valores
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Items Summary */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-primary">
                    {items.length}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {items.length === 1 ? "Item" : "Itens"}
                  </div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold">
                    {formatCurrency(subtotal)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Subtotal
                  </div>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {formatCurrency(finalValue)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Valor Final
                  </div>
                </div>
              </div>

              {/* Discount Configuration */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium flex items-center gap-2">
                    <Percent className="h-5 w-5" />
                    Desconto
                  </h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">
                      {usePercentageDiscount ? "Percentual" : "Valor Fixo"}
                    </span>
                    <Switch
                      checked={usePercentageDiscount}
                      onCheckedChange={handleDiscountTypeChange}
                    />
                  </div>
                </div>

                {usePercentageDiscount ? (
                  <FormField
                    control={control}
                    name="discount_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Percentual de Desconto</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              placeholder="0"
                              {...field}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                field.onChange(value);
                              }}
                              className="pr-12"
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <span className="text-muted-foreground">%</span>
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Percentual de desconto sobre o subtotal
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <FormField
                    control={control}
                    name="discount_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valor do Desconto</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0,00"
                            {...field}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          Valor fixo de desconto
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {hasDiscount && (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-sm font-medium text-green-800">
                      Valor do Desconto:
                    </span>
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      -{formatCurrency(calculatedDiscount)}
                    </Badge>
                  </div>
                )}
              </div>

              <Separator />

              {/* Tax Information */}
              {hasTax && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Impostos
                  </h4>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-800">
                          Taxa de Impostos
                        </div>
                        <div className="text-sm text-blue-600">
                          {taxRate}% sobre o valor com desconto
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        {formatCurrency(taxAmount)}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Final Summary */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Subtotal:</span>
                  <span className="font-medium">
                    {formatCurrency(subtotal)}
                  </span>
                </div>

                {hasDiscount && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>Desconto:</span>
                    <span className="font-medium">
                      -{formatCurrency(calculatedDiscount)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Base de Cálculo {hasTax ? "(sem impostos)" : ""}:
                  </span>
                  <span className="font-medium">
                    {formatCurrency(discountedSubtotal)}
                  </span>
                </div>

                {hasTax && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Impostos ({taxRate}%):</span>
                    <span className="font-medium">
                      {formatCurrency(taxAmount)}
                    </span>
                  </div>
                )}

                <Separator />

                <div className="flex items-center justify-between text-lg font-bold">
                  <span className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Valor Final:
                  </span>
                  <span className="text-primary text-xl">
                    {formatCurrency(finalValue)}
                  </span>
                </div>
              </div>

              {/* Additional Information */}
              {calculatedDiscount > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <div className="font-medium mb-1">Atenção:</div>
                    <div>
                      {usePercentageDiscount
                        ? `Desconto de ${discountPercentage}% aplicado sobre o subtotal.`
                        : `Desconto fixo de ${formatCurrency(discountAmount)} aplicado.`
                      }
                      {" "}O valor final já inclui os impostos{hasTax ? ` (${taxRate}%)` : ""}.
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Formas de Pagamento Sugeridas</h5>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div>• 50% à vista: {formatCurrency(finalValue * 0.5)}</div>
                    <div>• 50% em 30 dias: {formatCurrency(finalValue * 0.5)}</div>
                    <div>• Parcelado em 3x: {formatCurrency(finalValue / 3)} cada</div>
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <h5 className="font-medium mb-2">Estatísticas do Orçamento</h5>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div>• Ticket médio por item: {formatCurrency(subtotal / items.length)}</div>
                    <div>• Desconto médio: {hasDiscount ? formatCurrency(calculatedDiscount / items.length) : formatCurrency(0)}</div>
                    <div>• Carga tributária: {hasTax ? `${taxRate}%` : "0%"}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}